import { useId, useMemo, useState } from 'react';
import { useT } from '../../i18n';
import type { Vars } from '../../i18n';
import {
  BOOKING_DEFAULT_CONFIG,
  assetBookingEndTime,
  assetBookingWithinBooking,
  calcAssetBookingPrice,
  validateAssetBooking,
} from '../../engine';
import { toMinHHMM, fromMinHHMM } from '../../lib/date';
import type { Asset } from '../../lib/api/assets';
import type { AssetBooking, AssetBookingIntent } from '../../lib/api/assetBookings';
import type { AssetMutationResult } from '../../state/useAssets';
import { assetKindLabel } from './AssetEditor';
import { isOverrideRefusal } from '../../lib/api/bookingOverride';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

// ─────────────────────────────────────────────────────────────────────
// ONE ASSET BOOKING — create or edit (docs/domains/ASSET_BOOKING.md).
//
// The equipment counterpart of the court booking modal, deliberately smaller:
// which asset, which day, when and for how long, who, and — the one thing a
// court booking does not have — which court booking it rides with. The price
// is previewed with the same function the server charges with, and every
// refusal the server can give has a sentence here, keyed by its code.
// ─────────────────────────────────────────────────────────────────────

export interface AssetBookingModalProps {
  mode: 'create' | 'edit';
  /** Seed values (create) or the booking being edited. */
  initial?: Partial<AssetBooking> | null;
  assets: Asset[];
  assetBookings: AssetBooking[];
  /** The court bookings, for the «koble til banebooking» choice. */
  bookings?: any[];
  /** The centre's booking config — hours, step, durations, holidays. */
  config?: any;
  adminMode?: boolean;
  defaultDate: string;
  onClose: () => void;
  onSave: (intent: AssetBookingIntent) => Promise<AssetMutationResult<AssetBooking>>;
  onDelete?: (() => Promise<AssetMutationResult>) | null;
}

type Translate = (key: any, vars?: Vars) => string;

/** The sentence for an asset-booking refusal — the server's code, the catalog's words. */
export function assetBookingErrorMessage(
  t: Translate,
  r: { code?: string | null; error?: string; detail?: Record<string, unknown> | null },
  vars: Vars = {},
): string {
  const detail = (r.detail?.detail ?? r.detail ?? {}) as Record<string, unknown>;
  switch (r.code) {
    case 'asset_taken':
      return t('booking.assetBooking.error.asset_taken');
    case 'dead_gap':
      return t('booking.assetBooking.error.dead_gap');
    case 'outside_opening_hours':
      return t('booking.assetBooking.error.outside_opening_hours', {
        open: String(detail.openTime ?? ''),
        close: String(detail.closeTime ?? ''),
      });
    case 'booking_hours_not_set':
      return t('booking.assetBooking.error.booking_hours_not_set');
    case 'off_grid':
      return t('booking.assetBooking.error.off_grid');
    case 'end_before_start':
      return t('booking.assetBooking.error.end_before_start');
    case 'missing_fields':
      return t('booking.assetBooking.error.missing_fields');
    case 'asset_inactive':
      return t('booking.assetBooking.error.asset_inactive');
    case 'asset_not_found':
      return t('booking.assetBooking.error.asset_not_found');
    case 'asset_outside_booking':
      return t('booking.assetBooking.error.asset_outside_booking');
    case 'booking_not_found':
      return t('booking.assetBooking.error.booking_not_found');
    case 'payment_status_not_operator_controlled':
      return t('booking.assetBooking.error.payment_status_not_operator_controlled');
    case 'payment_status_contradicts_price':
      return t('booking.assetBooking.error.payment_status_contradicts_price');
    // The admin-mode override refused for want of «Booke utenom reglene» (#949).
    // Another right refused stays the generic sentence.
    case 'forbidden':
      return isOverrideRefusal(r.detail)
        ? t('booking.override.refused')
        : t('booking.assetBooking.error.generic');
    case 'asset_conflict':
      return t('booking.assetBooking.error.asset_conflict', { asset: String(vars.asset ?? '') });
    default:
      return r.error && !r.code
        ? t('booking.assets.error.generic', { error: String(r.error) })
        : t('booking.assetBooking.error.generic');
  }
}

const EXTRA_DURATIONS = [30, 60, 90, 120];

export function AssetBookingModal({
  mode,
  initial = null,
  assets,
  assetBookings,
  bookings = [],
  config = null,
  adminMode = false,
  defaultDate,
  onClose,
  onSave,
  onDelete = null,
}: AssetBookingModalProps) {
  const t = useT();
  const uid = safeId('asset-booking', useId());
  const ids = {
    asset: `${uid}-asset`,
    date: `${uid}-date`,
    start: `${uid}-start`,
    duration: `${uid}-duration`,
    attach: `${uid}-attach`,
    attachHint: `${uid}-attach-hint`,
    bookedBy: `${uid}-booked-by`,
    notes: `${uid}-notes`,
    status: `${uid}-status`,
    error: `${uid}-error`,
  };
  const cfg = { ...BOOKING_DEFAULT_CONFIG, ...(config || {}) };

  const bookable = assets.filter((a) => a.atThisVenue);
  const [assetId, setAssetId] = useState<string>(
    initial?.assetId ?? bookable.find((a) => a.active)?.id ?? bookable[0]?.id ?? '',
  );
  const [date, setDate] = useState<string>(initial?.date || defaultDate);
  const [startTime, setStartTime] = useState<string>(initial?.startTime || cfg.openTime || '10:00');
  const [durationMin, setDurationMin] = useState<number>(initial?.durationMin || 60);
  const [attachTo, setAttachTo] = useState<string>(initial?.bookingId ?? '');
  const [bookerName, setBookerName] = useState<string>(initial?.bookedBy?.name ?? '');
  const [notes, setNotes] = useState<string>(initial?.notes ?? '');
  const [paymentStatus, setPaymentStatus] = useState<'free' | 'pending' | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const endTime = assetBookingEndTime(startTime, durationMin);
  const asset = bookable.find((a) => a.id === assetId) ?? null;

  // The start times on offer: the centre's grid inside its hours; admin may
  // pick any half hour of the day, as for a court.
  const startOptions = useMemo(() => {
    const step = Number(cfg.stepMin) || 30;
    const open = adminMode ? 0 : toMinHHMM(cfg.openTime || '07:00');
    let close = adminMode ? 1440 : toMinHHMM(cfg.closeTime || '23:00');
    if (close <= open) close += 1440;
    const out: string[] = [];
    for (let m = open; m < close; m += step) out.push(fromMinHHMM(m));
    if (startTime && !out.includes(startTime)) out.push(startTime);
    return out;
  }, [cfg.stepMin, cfg.openTime, cfg.closeTime, adminMode, startTime]);

  const durationOptions = useMemo(() => {
    const allowed = Array.isArray(cfg.allowedDurations) ? (cfg.allowedDurations as number[]) : [];
    return [...new Set([...EXTRA_DURATIONS, ...allowed, durationMin])].sort((a, b) => a - b);
  }, [cfg.allowedDurations, durationMin]);

  // Court bookings this reservation could ride with: same day, and the court's
  // window holds the asset's. Named by the id the API speaks (`book_…`).
  const attachOptions = useMemo(
    () =>
      (bookings || []).filter(
        (b) =>
          b?.id && b.date === date && assetBookingWithinBooking({ date, startTime, endTime }, b),
      ),
    [bookings, date, startTime, endTime],
  );
  const attachable = attachTo && attachOptions.some((b) => String(b.id) === attachTo);

  const availability = (a: Asset) =>
    validateAssetBooking(
      {
        ...(initial?.id ? { id: initial.id } : {}),
        assetId: a.id,
        date,
        startTime,
        endTime,
      },
      assetBookings,
      { bufferMin: a.bufferMin ?? 0 },
    );

  const price = asset ? calcAssetBookingPrice(asset, date, startTime, endTime, cfg).total : 0;
  const derivedStatus = price > 0 ? 'pending' : 'free';

  const save = async () => {
    if (!asset) return;
    setBusy(true);
    setError(null);
    const r = await onSave({
      assetId: asset.id,
      date,
      startTime,
      durationMin,
      bookingId: attachable ? attachTo : null,
      bookedBy: { name: bookerName.trim(), phone: '', email: '' },
      notes: notes.trim() || null,
      ...(adminMode && paymentStatus ? { paymentStatus } : {}),
      adminOverride: adminMode,
    });
    setBusy(false);
    if (!r.success) setError(assetBookingErrorMessage(t, r));
  };

  const whoFor = (b: any) => b.title || b.bookedBy?.name || t('booking.assetBooking.anonymous');

  return (
    <Dialog
      heading={t(
        mode === 'create' ? 'booking.assetBooking.title.create' : 'booking.assetBooking.title.edit',
      )}
      onClose={onClose}
      className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5"
    >
      {bookable.length === 0 ? (
        <p className="text-sm text-ink-2">{t('booking.assetBooking.noAssets')}</p>
      ) : (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <div>
            <label
              htmlFor={ids.asset}
              className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
            >
              {t('booking.assetBooking.asset')}
            </label>
            <select
              id={ids.asset}
              className="input w-full"
              value={assetId}
              onChange={(e) => setAssetId(e.target.value)}
            >
              {bookable.map((a) => {
                const v = availability(a);
                const taken = !v.ok && (v.code === 'asset_taken' || v.code === 'dead_gap');
                return (
                  <option key={a.id} value={a.id} disabled={!a.active}>
                    {a.name} · {assetKindLabel(t, a.kind)}
                    {!a.active ? ` · ${t('booking.assets.inactive')}` : ''}
                    {a.active && taken ? ` · ${t('booking.assetBooking.unavailable')}` : ''}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label
                htmlFor={ids.date}
                className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
              >
                {t('booking.assetBooking.date')}
              </label>
              <input
                id={ids.date}
                type="date"
                className="input w-full"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div>
              <label
                htmlFor={ids.start}
                className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
              >
                {t('booking.assetBooking.start')}
              </label>
              <select
                id={ids.start}
                className="input w-full"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              >
                {startOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor={ids.duration}
                className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
              >
                {t('booking.assetBooking.duration')}
              </label>
              <select
                id={ids.duration}
                className="input w-full"
                value={String(durationMin)}
                onChange={(e) => setDurationMin(Number(e.target.value))}
              >
                {durationOptions.map((d) => (
                  <option key={d} value={String(d)}>
                    {t('booking.assetBooking.durationMin', { n: String(d) })}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label
              htmlFor={ids.attach}
              className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
            >
              {t('booking.assetBooking.attach')}
            </label>
            <select
              id={ids.attach}
              aria-describedby={ids.attachHint}
              className="input w-full"
              value={attachable ? attachTo : ''}
              onChange={(e) => setAttachTo(e.target.value)}
            >
              <option value="">{t('booking.assetBooking.attachNone')}</option>
              {attachOptions.map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {t('booking.assetBooking.attachOption', {
                    court: t('booking.view.courtName', { n: String(b.court) }),
                    start: b.startTime,
                    end: b.endTime,
                    who: whoFor(b),
                  })}
                </option>
              ))}
            </select>
            <div id={ids.attachHint} className="text-[10px] text-ink-3 mt-1">
              {t('booking.assetBooking.attachHint')}
            </div>
          </div>

          {adminMode && (
            <div>
              <label
                htmlFor={ids.bookedBy}
                className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
              >
                {t('booking.assetBooking.bookedBy')}
              </label>
              <input
                id={ids.bookedBy}
                className="input w-full"
                value={bookerName}
                onChange={(e) => setBookerName(e.target.value)}
              />
            </div>
          )}

          <div>
            <label
              htmlFor={ids.notes}
              className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
            >
              {t('booking.assetBooking.notes')}
            </label>
            <textarea
              id={ids.notes}
              className="input w-full"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="flex items-end justify-between gap-3 rounded-xl bg-bg-soft px-3 py-2">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-3">
                {t('booking.assetBooking.price')}
              </div>
              <div className="text-lg font-semibold text-ink-1">
                {price > 0
                  ? t('booking.assetBooking.priceKr', { n: String(price) })
                  : t('booking.assetBooking.free')}
              </div>
            </div>
            {adminMode && (
              <div>
                <label htmlFor={ids.status} className="block text-[10px] text-ink-3 mb-1">
                  {t('booking.assetBooking.paymentStatus')}
                </label>
                <select
                  id={ids.status}
                  className="input"
                  value={paymentStatus || derivedStatus}
                  onChange={(e) => setPaymentStatus(e.target.value as 'free' | 'pending')}
                >
                  <option value="free">{t('booking.assetBooking.status.free')}</option>
                  <option value="pending">{t('booking.assetBooking.status.pending')}</option>
                </select>
              </div>
            )}
          </div>

          {error && (
            <div id={ids.error} role="alert" className="text-xs text-rose">
              {error}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 pt-1">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="text-sm rounded-full px-4 py-2 border border-line text-ink-2"
              >
                {t('booking.cancel')}
              </button>
              {mode === 'edit' && onDelete && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    const r = await onDelete();
                    setBusy(false);
                    if (!r.success) setError(assetBookingErrorMessage(t, r));
                  }}
                  className="text-sm rounded-full px-4 py-2 border border-rose text-rose disabled:opacity-60"
                >
                  {t('booking.assetBooking.delete')}
                </button>
              )}
            </div>
            <button
              type="submit"
              disabled={busy || !asset}
              className="text-sm font-semibold rounded-full px-4 py-2 bg-lime text-ink-1 shadow hover:shadow-md disabled:opacity-60"
            >
              {busy ? t('booking.assetBooking.saving') : t('booking.assetBooking.save')}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
